(function () {
  'use strict';

  function createBundleGroupId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return 'bundle-' + Date.now() + '-' + Math.random().toString(16).slice(2);
  }

  function getFlavorMap(config) {
    var map = {};
    config.flavors.forEach(function (flavor) {
      map[flavor.key] = flavor;
    });
    return map;
  }

  function buildFlavorsSummary(selections, flavorMap) {
    var counts = {};
    selections.forEach(function (key) {
      counts[key] = (counts[key] || 0) + 1;
    });

    return Object.keys(counts)
      .map(function (key) {
        var flavor = flavorMap[key];
        return counts[key] + '× ' + (flavor ? flavor.label : key);
      })
      .join(', ');
  }

  function formatPickLine(quantity, sku, label) {
    var name = sku ? sku + ' (' + label + ')' : label;
    return quantity + '× ' + name;
  }

  function buildInventoryPlan(selections, flavorMap) {
    var counts = {};
    selections.forEach(function (key) {
      counts[key] = (counts[key] || 0) + 1;
    });

    return Object.keys(counts)
      .map(function (key) {
        var flavor = flavorMap[key];
        return formatPickLine(counts[key], flavor.sku, flavor.label);
      })
      .join(', ');
  }

  function bundleGiftsEnabled(config) {
    if (typeof config.giftsEnabled === 'boolean') {
      return config.giftsEnabled;
    }
    return Boolean(config.foundingGiftsEnabled);
  }

  function buildLaunchExtrasPlan(config, selectedExtras, isSubscription) {
    if (!bundleGiftsEnabled(config)) {
      return null;
    }

    var extras = (selectedExtras || config.launchExtrasFixed || config.launchExtras || []).slice();
    if (isSubscription) {
      extras = extras.concat(config.subscriptionExtrasFixed || []);
    }
    if (!extras.length) {
      return null;
    }

    return extras;
  }

  function getSelectedLaunchExtras(config) {
    return (config.launchExtrasFixed || config.launchExtras || []).slice();
  }

  function buildLaunchExtrasProperties(config, selectedExtras, isSubscription) {
    var plan = buildLaunchExtrasPlan(config, selectedExtras, isSubscription);
    if (!plan) return {};

    var picks = plan
      .map(function (extra) {
        return formatPickLine(extra.quantity || 1, extra.sku, extra.label);
      })
      .join(', ');

    var labels = plan
      .map(function (extra) {
        return extra.label;
      })
      .join(', ');

    return {
      _launch_extras: picks,
      'Free gifts': labels,
    };
  }

  function buildCartItems(selections, config, groupId, selectedExtras, isSubscription) {
    var flavorMap = getFlavorMap(config);
    var flavorsSummary = buildFlavorsSummary(selections, flavorMap);
    var properties = {
      _bundle_group: groupId,
      _bundle_primary: 'true',
      _bundle_gifts_enabled: bundleGiftsEnabled(config) ? 'true' : 'false',
      Bundle: config.bundleName,
      Flavors: flavorsSummary,
      _inventory_plan: buildInventoryPlan(selections, flavorMap),
    };

    if (bundleGiftsEnabled(config) && (config.subscriptionExtrasFixed || []).length) {
      properties._subscription_gift_plan = JSON.stringify(
        config.subscriptionExtrasFixed.map(function (extra) {
          return {
            variantId: extra.variantId,
            quantity: extra.quantity || 1,
          };
        })
      );
    }

    Object.assign(
      properties,
      buildLaunchExtrasProperties(config, selectedExtras, isSubscription)
    );

    selections.forEach(function (key, index) {
      var flavor = flavorMap[key];
      if (!flavor) return;
      properties['Pouch ' + (index + 1)] = flavor.label;
    });

    var items = [
      {
        id: config.bundleVariantId,
        quantity: 1,
        properties: properties,
      },
    ];

    if (isSubscription && bundleGiftsEnabled(config)) {
      (config.subscriptionExtrasFixed || []).forEach(function (extra) {
        if (!extra.variantId) return;
        items.push({
          id: extra.variantId,
          quantity: extra.quantity || 1,
          properties: {
            _bundle_group: groupId,
            _bundle_component: 'true',
            _bundle_gift: 'true',
            _first_subscription_gift: 'true',
          },
        });
      });
    }

    return items;
  }

  function removePaidGiftLines(cart, groupId) {
    var paidGiftLines = (cart.items || []).filter(function (item) {
      return (
        item.properties &&
        item.properties._bundle_group === groupId &&
        item.properties._bundle_gift === 'true' &&
        item.final_line_price > 0
      );
    });

    if (!paidGiftLines.length) return Promise.resolve(cart);

    var chain = Promise.resolve();
    paidGiftLines.forEach(function (item) {
      chain = chain.then(function () {
        return fetch(window.Shopify.routes.root + 'cart/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 0 }),
        });
      });
    });

    return chain
      .then(function () {
        return window.MuaCart.fetchCart();
      })
      .then(function (updatedCart) {
        window.MuaCart.renderCartDrawer(updatedCart);
        var error = new Error(
          'Your subscription bundle is in the cart, but the paid bottle was removed because its free-gift discount did not apply. Refresh and try again before checkout.'
        );
        error.bundleGiftDiscountFailed = true;
        throw error;
      });
  }

  function getSelectedSellingPlanId(form) {
    var input = form.querySelector('.js-selling-plan-input');
    if (!input || !input.value) return null;
    var planId = parseInt(input.value, 10);
    return Number.isNaN(planId) ? null : planId;
  }

  function initSellingPlanPicker(form) {
    form.querySelectorAll('.js-selling-plan-option').forEach(function (option) {
      option.addEventListener('click', function () {
        form.querySelectorAll('.js-selling-plan-option').forEach(function (el) {
          el.classList.remove('is-selected');
        });
        option.classList.add('is-selected');
        var radio = option.querySelector('input[type="radio"]');
        if (radio) radio.checked = true;
        var sellingPlanInput = form.querySelector('.js-selling-plan-input');
        if (sellingPlanInput) {
          sellingPlanInput.value = radio && radio.value ? radio.value : '';
        }
      });
    });
  }

  function availableQuantity(flavor) {
    if (!flavor.inventoryManagement) return null;
    var quantity = Number(flavor.inventoryQuantity);
    return Number.isFinite(quantity) ? Math.max(0, quantity) : 0;
  }

  function validateSelections(selections, config, stockError) {
    if (!config.flavors.length) {
      return 'Flavor products are not configured. Set them in Theme settings > Bundle flavors.';
    }

    if (!config.bundleVariantId) {
      return 'This bundle product is missing a variant. Save the product in Shopify admin and try again.';
    }

    if (selections.length !== config.pouchCount) {
      return 'Choose a flavor for each pouch.';
    }

    var flavorMap = getFlavorMap(config);
    var counts = {};
    for (var i = 0; i < selections.length; i += 1) {
      var key = selections[i];
      var flavor = flavorMap[key];
      counts[key] = (counts[key] || 0) + 1;
      var quantity = flavor && availableQuantity(flavor);
      if (!flavor || !flavor.available || (quantity !== null && counts[key] > quantity)) {
        return stockError;
      }
    }

    return null;
  }

  function initBundleForm() {
    var form = document.querySelector('.js-bundle-form');
    var configEl = document.getElementById('BundleConfigJSON');
    if (!form || !configEl) return;

    var config = JSON.parse(configEl.textContent);
    var flavorMap = getFlavorMap(config);
    var selects = form.querySelectorAll('.js-bundle-flavor-select');
    var summaryEl = form.querySelector('.js-bundle-summary');
    var errorEl = form.querySelector('.js-bundle-error');
    var submitBtn = form.querySelector('.js-bundle-submit');
    var stockError = form.getAttribute('data-error-stock') || 'One or more selected flavors no longer have enough stock.';
    var inventoryError = form.getAttribute('data-error-inventory') || 'Could not check flavor stock right now.';
    var stockLabel = form.getAttribute('data-stock-label') || '__COUNT__ available';
    var soldOutLabel = form.getAttribute('data-sold-out-label') || 'Sold out';
    var inventoryRequest = null;

    initSellingPlanPicker(form);

    function getSelections() {
      var selections = [];
      selects.forEach(function (select) {
        if (select.value) selections.push(select.value);
      });
      return selections;
    }

    function showError(message) {
      if (!errorEl) return;
      errorEl.textContent = message;
      errorEl.hidden = false;
    }

    function trimSelectionsToStock() {
      var counts = {};
      var changed = false;
      selects.forEach(function (select) {
        var key = select.value;
        if (!key) return;
        var flavor = flavorMap[key];
        var quantity = flavor && availableQuantity(flavor);
        if (!flavor || !flavor.available || (quantity !== null && (counts[key] || 0) >= quantity)) {
          select.value = '';
          changed = true;
          return;
        }
        counts[key] = (counts[key] || 0) + 1;
      });
      return changed;
    }

    function updateFlavorOptions() {
      var selectedCounts = {};
      getSelections().forEach(function (key) {
        selectedCounts[key] = (selectedCounts[key] || 0) + 1;
      });
      selects.forEach(function (select) {
        Array.from(select.options).forEach(function (option) {
          if (!option.value) return;
          var flavor = flavorMap[option.value];
          var quantity = flavor && availableQuantity(flavor);
          var selectedElsewhere = (selectedCounts[option.value] || 0) - (select.value === option.value ? 1 : 0);
          var remaining = quantity === null ? null : Math.max(0, quantity - selectedElsewhere);
          var inStock = Boolean(flavor && flavor.available && (remaining === null || remaining > 0));
          option.disabled = !inStock;
          option.textContent = flavor
            ? flavor.label + (inStock && remaining !== null
              ? ' (' + stockLabel.replace('__COUNT__', String(remaining)) + ')'
              : (!inStock ? ' (' + soldOutLabel + ')' : ''))
            : option.value + ' (' + soldOutLabel + ')';
        });
      });
    }

    function refreshInventory() {
      if (inventoryRequest) return inventoryRequest;
      var url = window.location.pathname + '?section_id=' + encodeURIComponent(config.sectionId);
      inventoryRequest = fetch(url, { credentials: 'same-origin', cache: 'no-store' })
        .then(function (response) {
          if (!response.ok) throw new Error(inventoryError);
          return response.text();
        })
        .then(function (html) {
          var sectionDocument = new DOMParser().parseFromString(html, 'text/html');
          var freshConfigEl = sectionDocument.getElementById('BundleConfigJSON');
          if (!freshConfigEl) throw new Error(inventoryError);
          var freshConfig = JSON.parse(freshConfigEl.textContent);
          if (!Array.isArray(freshConfig.flavors) || !freshConfig.flavors.length) {
            throw new Error(inventoryError);
          }
          config.flavors = freshConfig.flavors;
          flavorMap = getFlavorMap(config);
          if (trimSelectionsToStock()) showError(stockError);
          updateFlavorOptions();
          updateSummary();
        })
        .finally(function () {
          inventoryRequest = null;
        });
      return inventoryRequest;
    }

    function updateSummary() {
      var selections = getSelections();
      if (!summaryEl) return;

      if (selections.length === 0) {
        summaryEl.hidden = true;
        summaryEl.textContent = '';
        return;
      }

      summaryEl.textContent = buildFlavorsSummary(selections, flavorMap);
      summaryEl.hidden = false;
    }

    selects.forEach(function (select) {
      select.addEventListener('change', function () {
        if (errorEl) errorEl.hidden = true;
        if (trimSelectionsToStock()) showError(stockError);
        updateFlavorOptions();
        updateSummary();
        refreshInventory().catch(function () { showError(inventoryError); });
      });
      select.addEventListener('focus', function () {
        refreshInventory().catch(function () { showError(inventoryError); });
      });
    });

    document.querySelectorAll('.js-gallery-thumb').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var mainImage = document.querySelector('.js-product-main-image');
        if (!mainImage) return;
        document.querySelectorAll('.js-gallery-thumb').forEach(function (el) {
          el.classList.remove('is-active');
        });
        thumb.classList.add('is-active');
        mainImage.src = thumb.getAttribute('data-image-src');
        mainImage.alt = thumb.getAttribute('data-image-alt') || '';
      });
    });

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (errorEl) errorEl.hidden = true;

      if (getSelections().length !== config.pouchCount) {
        if (errorEl) {
          errorEl.textContent =
            form.getAttribute('data-error-flavors') ||
            'Choose a flavor for every pouch before adding to cart.';
          errorEl.hidden = false;
        }
        return;
      }

      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = form.getAttribute('data-adding-label') || 'Adding…';
      }

      var groupId;
      var sellingPlanId;
      refreshInventory()
        .then(function () {
          var selections = getSelections();
          if (selections.length !== config.pouchCount) throw new Error(stockError);
          var validationError = validateSelections(selections, config, stockError);
          if (validationError) throw new Error(validationError);
          groupId = createBundleGroupId();
          sellingPlanId = getSelectedSellingPlanId(form);
          var selectedExtras = getSelectedLaunchExtras(config);
          var items = buildCartItems(
            selections,
            config,
            groupId,
            selectedExtras,
            Boolean(sellingPlanId)
          );
          if (sellingPlanId) items[0].selling_plan = sellingPlanId;
          return fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: items }),
          });
        })
        .then(function (res) {
          return res.json().then(function (data) {
            if (!res.ok) throw new Error(data.description || data.message || 'Add to cart failed');
            return data;
          });
        })
        .then(function () {
          if (!window.MuaCart) return;
          return window.MuaCart.fetchCart()
            .then(function (cart) {
              if (!sellingPlanId) return cart;
              return removePaidGiftLines(cart, groupId);
            })
            .then(window.MuaCart.renderCartDrawer)
            .then(function () {
              if (!window.RechargeStorefrontConfig && window.MuaCart.openCart) {
                return window.MuaCart.openCart();
              }
            });
        })
        .catch(function (err) {
          if (errorEl) {
            errorEl.textContent = err.message || 'Could not add bundle to cart. Try again.';
            errorEl.hidden = false;
          }
        })
        .finally(function () {
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = form.getAttribute('data-add-label') || 'Add bundle to cart';
          }
        });
    });

    updateFlavorOptions();
    updateSummary();
    refreshInventory().catch(function () { showError(inventoryError); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBundleForm);
  } else {
    initBundleForm();
  }
})();
